<template>
  <div class="contents">
    <div class="layout">

      <UserListHeader title="아티스트 승인 관리" menu="아티스트 승인 관리"/>

      <div class="white-panel mt20">
        <DataTable
          :columns="columns"
          :rows="items"
          row-key="userUuid"
          searchable
          search-placeholder="아티스트 이름 (정확히 입력)"
          paged
          :page="page"
          :size="size"
          :total-elements="totalElements"
          :total-pages="totalPages"
          @update:page="onPage"
          @update:size="onSize"
          @search="onSearch"
        >
          <template #cell-no="{ index }">{{ index + 1 }}</template>

          <template #cell-userName="{ row }">
            <router-link :to="{ name: 'UserApprovalDetail', params: { userUuid: row.userUuid } }">
              {{ row.userName }}
            </router-link>
          </template>

          <template #cell-status="{ row }">
            <span class="status-badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
          </template>

          <template #cell-files="{ row }">
            <button @click="downloadFiles(row.userUuid, row.userName)" type="button"
                    class="button green-button small">다운로드</button>
          </template>

          <template #cell-actions="{ row }">
            <div v-if="editing[row.userUuid]" class="action-buttons">
              <button @click="approval(row.userUuid, row.userName, 'APPROVED')" type="button"
                      class="button blue-button small">승인</button>
              <button @click="openRejectModal(row.userUuid, row.userName)" type="button"
                      class="button red-button small">반려</button>
              <button @click="approval(row.userUuid, row.userName, 'PENDING')" type="button"
                      class="button navy-button small">보류</button>
            </div>
            <button v-else @click="editMode(row.userUuid)" type="button"
                    class="button green-button small">변경</button>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- 반려 사유 입력 모달 -->
    <div v-if="rejectModal.show" class="reason-modal-overlay" @click.self="closeRejectModal">
      <div class="reason-modal">
        <h3 class="reason-modal-title">반려 사유</h3>
        <p class="reason-modal-target">{{ rejectModal.userName }} 님을 반려합니다.</p>
        <select v-model="rejectModal.selected" class="reason-select">
          <option value="" disabled>사유를 선택하세요</option>
          <option v-for="(r, i) in rejectReasons" :key="i" :value="r">{{ r }}</option>
          <option value="__custom__">+ 직접 입력</option>
        </select>
        <input v-if="rejectModal.selected === '__custom__'" v-model="rejectModal.custom"
               type="text" maxlength="500" placeholder="반려 사유를 입력하세요"
               class="reason-custom-input"/>
        <div class="reason-modal-buttons">
          <button @click="closeRejectModal" type="button" class="reason-btn reason-btn-cancel">취소</button>
          <button @click="confirmReject" type="button" class="reason-btn reason-btn-confirm">반려 확정</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import UserListHeader from './UserListHeader.vue';
import {onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';
import {saveAs} from 'file-saver';
import JSZip from 'jszip';
import DataTable from '@/components/common/DataTable.vue';

const items = ref([]);
const editing = ref({}); // userUuid -> true (편집모드). 페이징해도 uuid 기준이라 안전

const page = ref(0);
const size = ref(20);
const totalElements = ref(0);
const totalPages = ref(0);
const search = ref('');

const rejectReasons = ref([]); // 반려 사유 드롭다운 (시드 ∪ 기존 입력)
const rejectModal = ref({show: false, userUuid: '', userName: '', selected: '', custom: ''});

const columns = [
  { key: 'no', label: 'NO', minWidth: '50px' },
  { key: 'userName', label: '이름', minWidth: '90px' },
  { key: 'verificationMethod', label: '인증방식', minWidth: '90px' },
  { key: 'reqAt', label: '승인요청일시', minWidth: '160px' },
  { key: 'status', label: '처리결과', minWidth: '90px', align: 'center' },
  { key: 'doneAt', label: '처리일시', minWidth: '160px' },
  { key: 'doneBy', label: '처리자 ID', minWidth: '110px' },
  { key: 'files', label: '활동자료', minWidth: '100px', align: 'center' },
  { key: 'actions', label: '', minWidth: '210px' },
];

const STATUS_LABELS = {APPROVED: '승인', REJECTED: '반려', PENDING: '보류'};
const statusLabel = (s) => STATUS_LABELS[s] || '';
const statusClass = (s) => ({
  'is-approved': s === 'APPROVED',
  'is-rejected': s === 'REJECTED',
  'is-pending': s === 'PENDING',
});

const fetchItems = async () => {
  try {
    const response = await apiClient.get('/users/approvals/paged', {
      params: { page: page.value, size: size.value, filter: search.value || undefined },
    });
    const d = response.data;
    items.value = d.content ?? [];
    totalElements.value = d.totalElements ?? 0;
    totalPages.value = d.totalPages ?? 0;
    // 미처리(상태 null) 행은 기본 편집모드
    editing.value = {};
    items.value.forEach(it => {
      if (it.status === null || it.status === undefined) {
        editing.value[it.userUuid] = true;
      }
    });
  } catch (error) {
    console.error('Error:', error);
  }
};

const onPage = (p) => {
  page.value = p;
  fetchItems();
};
const onSize = (s) => {
  size.value = s;
  page.value = 0;
  fetchItems();
};
const onSearch = (keyword) => {
  search.value = keyword;
  page.value = 0;
  fetchItems();
};

const fetchRejectReasons = async () => {
  await apiClient.get('/users/approvals/reject-reasons')
  .then(response => {
    rejectReasons.value = response.data ?? [];
  })
  .catch(error => {
    console.error('Error:', error);
  });
};

// reason: 반려 시에만 전달 (선택)
const approval = async (userUuid, userName, approvalString, reason) => {
  if (confirm(userName + ' 사용자를 ' + approvalString + ' 하시겠습니까?')) {
    await apiClient.put(`/users/${userUuid}/approvals`, {
      status: approvalString,
      reason: reason ?? null,
    })
    .then(() => {
      alert(approvalString + ' 되었습니다.')
    })
    .catch(error => {
      console.error('Error:', error);
    });
    await fetchItems();
  }
};

const openRejectModal = (userUuid, userName) => {
  rejectModal.value = {show: true, userUuid, userName, selected: '', custom: ''};
};

const closeRejectModal = () => {
  rejectModal.value.show = false;
};

const confirmReject = async () => {
  const {selected, custom, userUuid, userName} = rejectModal.value;
  const reason = (selected === '__custom__' ? custom : selected).trim();
  if (!reason) {
    alert('반려 사유를 선택하거나 입력해 주세요.');
    return;
  }
  closeRejectModal();
  await approval(userUuid, userName, 'REJECTED', reason);
  await fetchRejectReasons(); // 새로 직접입력한 사유가 다음부터 드롭다운에 보이도록 갱신
};

const editMode = (userUuid) => {
  editing.value[userUuid] = true;
};

const downloadFiles = async (userUuid, userName) => {
  await apiClient.get(`/users/${userUuid}/approvals/file-urls`)
  .then(response => {
    const fileUrls = response.data.urls ?? [];
    downloadAttachmentsAndCreateZip(fileUrls, userName);
  })
  .catch(error => {
    console.error('Error:', error);
  });
}

const getFileNameFromUrl = (presignedUrl) => {
  const encodedFileName = presignedUrl.split('/').pop()?.split('?')[0];
  return encodedFileName ? decodeURIComponent(encodedFileName) : null;
};

async function downloadAttachmentsAndCreateZip(urls, userName) {
  try {
    const zip = new JSZip();

    const fetchPromises = urls.map(url =>
        fetch(url).then(response =>
            response.blob().then(blob => ({
              blob,
              fileName: getFileNameFromUrl(url),
            })),
        ),
    );

    const files = await Promise.all(fetchPromises);

    files.forEach(({blob, fileName}) => {
      if (fileName) {
        zip.file(fileName, blob);
      }
    });

    zip.generateAsync({type: 'blob'}).then(function (content) {
      saveAs(content, userName + '.zip');
    });
  } catch (error) {
    console.error('첨부파일 다운로드 중 오류 발생:', error);
  }
}

onMounted(async () => {
  await fetchItems();
  await fetchRejectReasons();
});
</script>

<style scoped>
.action-buttons {
  display: flex;
  gap: 8px;
}
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
  background: #eee;
  color: #555;
}
.status-badge.is-approved { background: #e6f4ea; color: #1e7e34; }
.status-badge.is-rejected { background: #fde7e9; color: #c0392b; }
.status-badge.is-pending  { background: #eef1fb; color: #3b4cca; }

/* 반려 사유 모달 */
.reason-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.reason-modal {
  background: #fff;
  width: 420px;
  max-width: 90%;
  padding: 28px 24px;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.reason-modal-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px;
}
.reason-modal-target {
  font-size: 14px;
  color: #555;
  margin: 0 0 16px;
}
.reason-select,
.reason-custom-input {
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}
.reason-custom-input {
  margin-top: 10px;
}
.reason-modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.reason-btn {
  height: 40px;
  padding: 0 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
}
.reason-btn-cancel {
  background: #fff;
  border-color: #ccc;
  color: #555;
}
.reason-btn-cancel:hover {
  background: #f5f5f5;
}
.reason-btn-confirm {
  background: #ef4452;
  color: #fff;
}
.reason-btn-confirm:hover {
  background: #d93644;
}
</style>
